import React from 'react';
import { graphql, PageProps } from 'gatsby';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { css } from '@emotion/react';

import Container from '../components/atoms/Container';
import Layout from '../components/layout';
import SplitHero from '../components/blocks/hero/split-hero';
import paddingDefault from '../components/utils/paddingDefault';
import Link from '../components/primitives/link';
import Seo from '../components/utils/Seo';
import CtaProvider from '../contexts/franchise/cta-provider';
import { colors } from '../components/primitives/colors';
import transformDynamic from './franchise/utils/transform-dynamic';
import { PageTemplate } from '../utils/match-page-template';

const styles = {
    background: css({
        background: colors.grey100,
    }),
    divider: css({
        marginTop: '24px',
        marginBottom: '24px',
    }),
    listHeading: css({
        fontSize: '20px',
        lineHeight: 1,
        color: colors.grey800,
        paddingBottom: '16px',
        width: '100%',
    }),
    metroName: css({
        listStyleType: 'disc',
    }),
    metroList: css({
        marginLeft: '16px',
    }),
};

export type MetroStatePageData = Queries.MetroStateTemplateQuery;

export interface MetroStatePageContext {
    slug: string
    state: string
    templateName: PageTemplate.MetroState
}

const MetroState: React.FC<PageProps<MetroStatePageData, MetroStatePageContext>> = ({ data, pageContext }) => {
    const { cmsMetroStateSections: cmsData } = data;
    const { slug: stateSlug } = pageContext;

    if (!data.cmsMetroParents) return null;

    const { seo, title: stateName } = data.cmsMetroParents;
    const franchises = data.allFranchise.nodes;
    const metroPages = data.allCmsMetroPage.nodes;
    const uniqueFranchises = uniqBy(franchises, 'name');
    const replacements = { stateName };

    if (!cmsData) return null;
    if (!seo) return null;

    const heroData = transformDynamic(
        {
            heading: cmsData.headline,
            subheading: cmsData.subheadline,
            copy: cmsData.copy,
            image: seo?.meta_image || cmsData.image,
        },
        replacements,
    );

    const metaTitle = `SERVPRO Locations in ${stateName}`;
    const metaDesc = `Wherever you are located, SERVPRO is Here to Help. We service all cities and zip codes in ${stateName}.`;

    return (
        <CtaProvider hideFranchiseLink>
            <Layout>
                <Seo
                    title={seo.meta_title || metaTitle}
                    description={seo.meta_description || metaDesc}
                    imageUrl={seo?.meta_image?.url || cmsData.image?.url}
                />

                <SplitHero {...heroData} />
                <div css={[paddingDefault()]} style={{ backgroundColor: colors.grey100 } as React.CSSProperties}>
                    <Container>
                        <div>
                            <h3 css={styles.listHeading}>
                                There is always a SERVPRO nearby to help make your property damage
                                &quot;Like it never even happened.&quot;
                            </h3>

                            <ul css={styles.metroList}>
                                {orderBy(metroPages, ['city']).map(metro => (
                                    <li css={styles.metroName} key={metro.city}>
                                        <Link to={`/locations${stateSlug}${metro.url}`}>
                                            {metro.city}
                                        </Link>
                                    </li>
                                ))}
                            </ul>

                            <hr css={styles.divider} />

                            <ul css={styles.metroList}>
                                {orderBy(uniqueFranchises, ['name']).map((fr) => {
                                    if (fr && fr.name) {
                                        return (
                                            <li css={styles.metroName} key={fr.name}>
                                                <Link to={`${fr.permalink}`} title={fr.name}>
                                                    {fr.name}
                                                </Link>
                                            </li>
                                        );
                                    }
                                    return null;
                                })}
                            </ul>
                        </div>
                    </Container>
                </div>
            </Layout>
        </CtaProvider>
    );
};

export const query = graphql`
  query MetroStateTemplate ($state: String, $slug: String!) {
    cmsMetroParents(url: { eq: $slug }) {
      title
      canadian_province
      seo {
        meta_title
        meta_description
        meta_image {
          filename
          title
          url
          description
          contentStackAsset(width: 1440)
        }
      }
    }
    allFranchise(filter: { address: { operatingState: { eq: $state }}}) {
      nodes {
        permalink
        name
      }
    }
    allCmsMetroPage(filter: {state: { elemMatch: { url: {eq: $slug }}}}) {
      nodes {
        city
        url
        franchise_number
      }
    }
    cmsMetroStateSections {
      headline
      subheadline
      copy
      image {
        filename
        title
        url
        description
        contentStackAsset(width: 720)
      }
      seo {
        meta_title
        meta_description
      }
    }
  }
`;

export default MetroState;
